import React, { useEffect, useRef, useState } from 'react';
import * as Scrivito from 'scrivito';
import SplitFlap from './SplitFlap';
import { LANGUAGES, activateTranslation, revertTranslation } from '../lib/universalTranslator';
import { pageLang } from '../lib/localize';

// Idle animation on the button itself — a small split-flap board cycling
// language codes, so the trigger for the gimmick already looks like the
// gimmick (and reuses the site's one signature component rather than a stock
// globe icon).
const IDLE_CODES = ['DE', 'EN', 'FR', 'ES', 'IT', 'PT', 'PL', 'TR', 'RU', 'JA', 'ZH', 'AR'];

// Every string the visitor sees in this UI, per page language. The component
// is mounted once, outside the page-routed subtree, so it resolves the page's
// language itself (src/lib/localize) — the old hardcoded-German version was
// exactly the "untranslated text on the wrong language's page" bug class the
// InquiryWidget DE_FALLBACKS pattern was created to prevent, missed here.
const STRINGS = {
  de: {
    close: 'Schließen',
    translated: 'Maschinell übersetzt',
    partial: 'teilweise',
    original: 'Original',
    fab: 'Universal Translator — Seite in eine andere Sprache übersetzen',
    // Shown in the success pill so it's obvious which engine actually ran —
    // useful while keeping an eye on DeepL's monthly quota.
    engines: { 'on-device': 'lokale KI', deepl: 'DeepL', mymemory: 'MyMemory', cache: 'Cache' },
    errors: {
      _default: 'Übersetzung gerade nicht verfügbar.',
      deepl_quota_exceeded: 'Übersetzungskontingent für diesen Monat erreicht — bitte später erneut versuchen.',
      mymemory_quota: 'Kostenloses Tageslimit der Übersetzung erreicht — bitte später erneut versuchen.',
      mymemory_bad_response: 'Übersetzung gerade nicht verfügbar.',
      mymemory_request_failed: 'Übersetzung gerade nicht erreichbar — bitte später erneut versuchen.',
    },
  },
  en: {
    close: 'Close',
    translated: 'Machine-translated',
    partial: 'partial',
    original: 'Original',
    fab: 'Universal Translator — translate this page into another language',
    engines: { 'on-device': 'on-device AI', deepl: 'DeepL', mymemory: 'MyMemory', cache: 'Cache' },
    errors: {
      _default: 'Translation is currently unavailable.',
      deepl_quota_exceeded: 'Monthly translation quota reached — please try again later.',
      mymemory_quota: 'Daily free translation limit reached — please try again later.',
      mymemory_bad_response: 'Translation is currently unavailable.',
      mymemory_request_failed: 'Translation service unreachable — please try again later.',
    },
  },
};

const UniversalTranslatorButton = Scrivito.connect(() => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState('idle'); // idle | translating | done | partial | error
  const [activeLang, setActiveLang] = useState(null);
  const [activeEngine, setActiveEngine] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [pickedCode, setPickedCode] = useState(null);
  // Persists across activations on purpose: re-translating into a different
  // language must always start from the true original text, never from a
  // previously translated string.
  const originalsRef = useRef(new Map());
  const rootRef = useRef(null);
  const panelRef = useRef(null);
  const fabRef = useRef(null);

  const s = STRINGS[pageLang()] || STRINGS.en;

  // While the panel is open: move focus into it, close on Escape (returning
  // focus to the trigger), and close on any click outside the widget —
  // standard dialog behavior. Without it, keyboard users had no way out of
  // the 18-chip panel short of tabbing through all of them, and mouse clicks
  // elsewhere on the page did nothing at all.
  useEffect(() => {
    if (!open) return undefined;
    panelRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        fabRef.current?.focus();
      }
    };
    const onPointerDown = (e) => {
      if (!rootRef.current || !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  // Editors must only ever see and edit the real source content.
  if (Scrivito.isInPlaceEditingActive()) return null;

  const busy = status === 'translating';

  const translateTo = async (code) => {
    if (code === activeLang) return; // already showing this language
    setStatus('translating');
    setErrorCode(null);
    try {
      const { partial, engine } = await activateTranslation({
        lang: code,
        originals: originalsRef.current,
      });
      setActiveLang(code);
      setActiveEngine(engine);
      setStatus(partial ? 'partial' : 'done');
    } catch (err) {
      setStatus('error');
      setErrorCode(err.code || 'translate_failed');
    }
  };

  // A brief confirmation pulse on the picked chip before the panel closes —
  // without it the panel just vanishes on click, which reads as the click
  // having missed rather than having been received.
  const handleChipClick = async (code) => {
    setPickedCode(code);
    await new Promise((resolve) => setTimeout(resolve, 160));
    setPickedCode(null);
    setOpen(false);
    // Close the dialog properly: hand focus back to the trigger.
    fabRef.current?.focus();
    translateTo(code);
  };

  const handleRevert = () => {
    revertTranslation(originalsRef.current);
    originalsRef.current = new Map();
    setActiveLang(null);
    setActiveEngine(null);
    setStatus('idle');
  };

  const activeLabel = LANGUAGES.find((l) => l.code === activeLang)?.label;

  return (
    <div className="tt-ut" data-no-translate="true" ref={rootRef}>
      {status === 'error' && (
        <div className="tt-ut__pill">
          <span>{s.errors[errorCode] || s.errors._default}</span>
          <button type="button" onClick={() => setStatus('idle')} aria-label={s.close}>×</button>
        </div>
      )}

      {(status === 'done' || status === 'partial') && (
        <div className="tt-ut__pill">
          <span>
            ⇄ {s.translated} · {activeLabel} · {s.engines[activeEngine] || activeEngine}
            {status === 'partial' ? ` · ${s.partial}` : ''}
          </span>
          <button type="button" onClick={handleRevert}>{s.original}</button>
        </div>
      )}

      {open && (
        <div className="tt-ut__panel" ref={panelRef} tabIndex={-1}>
          <div className="tt-ut__panel-title">Universal Translator</div>
          <div className="tt-ut__grid">
            {LANGUAGES.map((l, i) => (
              <button
                key={l.code}
                type="button"
                className={`tt-ut__chip${pickedCode === l.code ? ' tt-ut__chip--picked' : ''}`}
                style={{ '--tt-chip-delay': `${i * 22}ms` }}
                onClick={() => handleChipClick(l.code)}
              >
                <span className="tt-ut__chip-code">{l.code.toUpperCase()}</span>
                <span className="tt-ut__chip-label">{l.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <button
        type="button"
        className="tt-ut__fab"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label={s.fab}
        disabled={busy}
        ref={fabRef}
      >
        <SplitFlap words={busy ? ['···'] : IDLE_CODES} size="sm" interval={1600} />
        <span className="tt-ut__fab-label">Universal<br />Translator</span>
      </button>

      {busy && <div className="tt-ut__scan" aria-hidden="true" />}
    </div>
  );
});

export default UniversalTranslatorButton;
